// Utility functions for calculating localized prices from Swedish SEK base prices.
// Uses real-time exchange rates with caching and fallback to static rates.

// Fallback static rates (used if API fails or during initial load)
const STATIC_RATES = {
  sv: { currency: 'SEK', rateFromSek: 1 },
  en: { currency: 'USD', rateFromSek: 0.095 },
  da: { currency: 'DKK', rateFromSek: 0.65 },
  de: { currency: 'EUR', rateFromSek: 0.088 },
  es: { currency: 'EUR', rateFromSek: 0.088 },
  fr: { currency: 'EUR', rateFromSek: 0.088 },
  it: { currency: 'EUR', rateFromSek: 0.088 },
  fi: { currency: 'EUR', rateFromSek: 0.088 },
  no: { currency: 'NOK', rateFromSek: 1.0 },
  pl: { currency: 'PLN', rateFromSek: 0.39 },
  tr: { currency: 'TRY', rateFromSek: 0.30 },
};

// Cache settings
const CACHE_KEY = 'qfc_exchange_rates';
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

// Currency mapping: language code -> target currency code for API
const LANGUAGE_TO_CURRENCY = {
  sv: 'SEK',
  en: 'USD',
  da: 'DKK',
  de: 'EUR',
  es: 'EUR',
  fr: 'EUR',
  it: 'EUR',
  fi: 'EUR',
  no: 'NOK',
  pl: 'PLN',
  tr: 'TRY',
};

/**
 * Fetch exchange rates from API
 * Using exchangerate-api.com (free tier, no API key required)
 */
async function fetchExchangeRates() {
  try {
    // Using exchangerate-api.com free endpoint (no API key needed)
    // This gets rates relative to USD, so we need to convert SEK -> USD -> target currency
    const response = await fetch('https://api.exchangerate-api.com/v4/latest/USD');
    
    if (!response.ok) {
      throw new Error('Failed to fetch exchange rates');
    }
    
    const data = await response.json();
    const rates = data.rates;
    
    // Get SEK to USD rate
    const sekToUsd = rates.SEK ? 1 / rates.SEK : 0.095; // fallback if SEK not found
    
    // Calculate rates from SEK to all target currencies
    const exchangeRates = {};
    
    Object.entries(LANGUAGE_TO_CURRENCY).forEach(([lang, currency]) => {
      if (currency === 'SEK') {
        exchangeRates[lang] = { currency: 'SEK', rateFromSek: 1 };
      } else if (currency === 'USD') {
        exchangeRates[lang] = { currency: 'USD', rateFromSek: sekToUsd };
      } else {
        // Convert SEK -> USD -> target currency
        const usdToTarget = rates[currency] || STATIC_RATES[lang].rateFromSek / sekToUsd;
        const sekToTarget = sekToUsd * usdToTarget;
        exchangeRates[lang] = { currency, rateFromSek: sekToTarget };
      }
    });
    
    // Cache the rates with timestamp
    const cacheData = {
      rates: exchangeRates,
      timestamp: Date.now(),
    };
    localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
    
    return exchangeRates;
  } catch (error) {
    console.warn('Failed to fetch exchange rates, using cached or static rates:', error);
    return null;
  }
}

/**
 * Get exchange rates (from cache or API)
 */
async function getExchangeRates() {
  // Try to get from cache first
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) {
      const cacheData = JSON.parse(cached);
      const age = Date.now() - cacheData.timestamp;
      
      // If cache is still valid, use it
      if (age < CACHE_DURATION && cacheData.rates) {
        return cacheData.rates;
      }
    }
  } catch (error) {
    console.warn('Error reading cache:', error);
  }
  
  // Cache expired or doesn't exist, fetch new rates
  const rates = await fetchExchangeRates();
  
  // If fetch failed, try to use stale cache or fallback to static
  if (!rates) {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const cacheData = JSON.parse(cached);
        if (cacheData.rates) {
          return cacheData.rates; // Use stale cache
        }
      }
    } catch (error) {
      // Ignore
    }
    
    // Last resort: use static rates
    return STATIC_RATES;
  }
  
  return rates;
}

// Initialize rates (fetch in background, don't block)
let exchangeRatesPromise = null;
let currentRates = STATIC_RATES; // Start with static rates

// Pre-fetch rates when module loads
if (typeof window !== 'undefined') {
  exchangeRatesPromise = getExchangeRates().then(rates => {
    currentRates = rates;
    return rates;
  }).catch(() => {
    // Keep static rates on error
    return STATIC_RATES;
  });
}

/**
 * Get current exchange rate config for a language
 */
function getCurrencyConfig(languageCode) {
  return currentRates[languageCode] || STATIC_RATES[languageCode] || STATIC_RATES.sv;
}

/**
 * Round a numeric amount down to the nearest even integer.
 *
 * Examples:
 *  - 99  -> 98
 *  - 100 -> 100
 *  - 101 -> 100
 */
export function roundDownToNearestEven(amount) {
  if (Number.isNaN(amount)) return 0;
  const floored = Math.floor(amount);
  return floored % 2 === 0 ? floored : floored - 2;
}

/**
 * Calculate localized price based on a Swedish (SEK) base price.
 * Uses real-time exchange rates with caching.
 *
 * @param {number} baseSek - Base price in SEK.
 * @param {string} languageCode - i18n language code, e.g. 'sv', 'en', 'da', 'de', 'pl', 'tr'.
 * @returns {{ amount: number, currency: string }} - Rounded-down even amount and currency code.
 */
export function calculateLocalizedPrice(baseSek, languageCode) {
  if (typeof baseSek !== 'number' || !Number.isFinite(baseSek)) {
    throw new Error('baseSek must be a finite number');
  }

  const config = getCurrencyConfig(languageCode);
  const raw = baseSek * config.rateFromSek;
  
  // Don't round down if it's the base currency (SEK) - keep original price
  const amount = (config.rateFromSek === 1 && config.currency === 'SEK') 
    ? raw 
    : roundDownToNearestEven(raw);

  return {
    amount,
    currency: config.currency,
  };
}

/**
 * Format a monthly price string, e.g. "995 kr" or "88 €".
 * Returns just the price part without "/month" suffix - add that in the UI with i18n.
 */
export function formatLocalizedPrice(baseSek, languageCode) {
  const { amount, currency } = calculateLocalizedPrice(baseSek, languageCode);

  // Simple symbol mapping; adjust as needed.
  const symbols = {
    SEK: 'kr',
    DKK: 'kr',
    NOK: 'kr',
    USD: '$',
    EUR: '€',
    PLN: 'zł',
    TRY: '₺',
  };

  const symbol = symbols[currency] || currency;

  return `${amount} ${symbol}`;
}

/**
 * Format a monthly price string with "/month" suffix, e.g. "995 kr /mån" or "88 € /month".
 * Uses generic "/month" - for localized month text, use formatLocalizedPrice and add i18n suffix.
 */
export function formatLocalizedMonthlyPrice(baseSek, languageCode) {
  const price = formatLocalizedPrice(baseSek, languageCode);
  return `${price} /month`;
}

/**
 * Refresh exchange rates manually (useful for admin or manual refresh)
 */
export async function refreshExchangeRates() {
  exchangeRatesPromise = getExchangeRates().then(rates => {
    currentRates = rates;
    return rates;
  }).catch(() => {
    return STATIC_RATES;
  });
  return exchangeRatesPromise;
}

/**
 * Get current exchange rates (for debugging or display)
 */
export function getCurrentRates() {
  return currentRates;
}
